import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { UserPlus, Smartphone, Trophy, ArrowRight, Sparkles, CheckCircle2, Rocket } from 'lucide-react';
import { CardCorner, TranslatedText } from '../ui';

const steps = [
  { icon: UserPlus, key: 'step1', number: 1 },
  { icon: Smartphone, key: 'step2', number: 2 },
  { icon: Trophy, key: 'step3', number: 3 },
];

export function HowItWorks() {
  const { t: _t } = useTranslation();

  return (
    <section id="how-it-works" className="relative py-24 md:py-32 overflow-hidden bg-transparent">
      {/* Semi-transparent overlay for readability */}
      <div className="absolute inset-0 bg-brand-darkNavy/50 backdrop-blur-[2px]" />
      
      {/* Glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[1000px] h-[600px] bg-brand-yellow/5 rounded-full blur-[150px]" />

      <div className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center max-w-3xl mx-auto mb-20"
        >
          <motion.div 
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-brand-yellow/10 border border-brand-yellow/30 mb-6"
            whileHover={{ scale: 1.05 }}
          >
            <Sparkles className="w-4 h-4 text-brand-yellow" />
            <span className="text-sm font-bold text-brand-yellow uppercase tracking-wider">
              <TranslatedText i18nKey="howItWorks.badge" type="fade" duration={0.4} />
            </span>
          </motion.div>
          
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-black text-white mb-6 tracking-tight">
            <TranslatedText 
              i18nKey="howItWorks.title" 
              type="digital"
              duration={0.7}
            />
          </h2>
          <p className="text-lg md:text-xl text-white/80 font-medium mb-4">
            <TranslatedText 
              i18nKey="howItWorks.description" 
              type="fadeWords"
              duration={0.4}
              stagger={0.04}
            />
          </p>
          <p className="text-base md:text-lg text-dark-300">
            <TranslatedText 
              i18nKey="howItWorks.subtitle" 
              type="fade"
              duration={0.5}
            />
          </p>
        </motion.div>

        {/* Steps - Horizontal Timeline */}
        <div className="relative">
          {/* Connection line */}
          <div className="hidden lg:block absolute top-1/2 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-brand-yellow/30 to-transparent -translate-y-1/2" />
          
          <div className="grid lg:grid-cols-3 gap-8 lg:gap-12">
            {steps.map((step, index) => (
              <motion.div
                key={step.key}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.2 }}
                className="relative"
              >
                {/* Card */}
                <motion.div
                  whileHover={{ y: -10, scale: 1.02 }}
                  transition={{ type: 'spring', stiffness: 300 }}
                  className="relative bg-brand-navy/80 backdrop-blur-xl border border-brand-lightBlue/30 rounded-2xl p-8 overflow-hidden group hover:border-brand-yellow/50 transition-colors"
                >
                  {/* Corner decorations */}
                  <CardCorner position="top-left" />
                  <CardCorner position="bottom-right" />
                  
                  {/* Step number - Large background */}
                  <div className="absolute -right-4 -top-4 text-[120px] font-black text-brand-yellow/5 leading-none pointer-events-none select-none">
                    {step.number}
                  </div>
                  
                  {/* Step indicator */}
                  <div className="relative z-10 flex items-center gap-4 mb-6">
                    <motion.div 
                      className="w-16 h-16 rounded-2xl bg-brand-yellow/10 border-2 border-brand-yellow/50 flex items-center justify-center group-hover:bg-brand-yellow/20 group-hover:border-brand-yellow transition-all"
                      whileHover={{ rotate: 360 }}
                      transition={{ duration: 0.6 }}
                    >
                      <span className="text-2xl font-black text-brand-yellow">{step.number}</span>
                    </motion.div>
                    
                    {index < steps.length - 1 && (
                      <ArrowRight className="w-6 h-6 text-brand-yellow/30 hidden lg:block absolute -right-10 top-1/2 -translate-y-1/2" />
                    )}
                  </div>
                  
                  {/* Icon */}
                  <div className="relative z-10 mb-4">
                    <step.icon className="w-8 h-8 text-brand-yellow" />
                  </div>
                  
                  {/* Content */}
                  <h3 className="relative z-10 text-xl font-bold text-white mb-3 group-hover:text-brand-yellow transition-colors">
                    <TranslatedText 
                      i18nKey={`howItWorks.${step.key}Title`}
                      type="slideWords"
                      duration={0.4}
                      stagger={0.06}
                    />
                  </h3>
                  <p className="relative z-10 text-dark-400 leading-relaxed mb-4">
                    <TranslatedText 
                      i18nKey={`howItWorks.${step.key}Desc`}
                      type="fade"
                      duration={0.5}
                    />
                  </p>
                  
                  {/* Bullet points */}
                  <div className="relative z-10 space-y-2 mb-4">
                    <div className="flex items-center gap-2 text-dark-300">
                      <CheckCircle2 className="w-4 h-4 text-brand-yellow flex-shrink-0" />
                      <span className="text-sm">
                        <TranslatedText 
                          i18nKey={`howItWorks.${step.key}Bullet1`}
                          type="fade"
                          duration={0.4}
                        />
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-dark-300">
                      <CheckCircle2 className="w-4 h-4 text-brand-yellow flex-shrink-0" />
                      <span className="text-sm">
                        <TranslatedText 
                          i18nKey={`howItWorks.${step.key}Bullet2`}
                          type="fade"
                          duration={0.4}
                        />
                      </span>
                    </div>
                  </div>

                  {/* CTA */}
                  <div className="relative z-10 flex items-center gap-2 text-brand-yellow font-semibold text-sm">
                    <Rocket className="w-4 h-4" />
                    <span>
                      <TranslatedText 
                        i18nKey={`howItWorks.${step.key}Cta`}
                        type="fade"
                        duration={0.4}
                      />
                    </span>
                  </div>
                  
                  {/* Hover glow */}
                  <div className="absolute inset-0 bg-gradient-to-br from-brand-yellow/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                </motion.div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
